import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FiCheck, FiSend, FiRefreshCw, FiMail, FiExternalLink, FiFlag, FiChevronLeft, FiX, FiDollarSign, FiAlertTriangle } from 'react-icons/fi';
import { addMonths } from 'date-fns';
import { uuid } from 'uuidv4';
import api from '../../../../services/api';
import { useToast } from '../../../../hooks/Toast';
import { useAuth } from '../../../../hooks/Auth';
import {
  Container, Header, Tabs, Body, Footer, Alert, Tiles, Tile, Filters, Seg, Chip,
  SummaryRight, TableWrap, Grid, BtnLink, IconBtn, Tag, Selo, BulkBar, BulkBtn,
  BtnCancel, BtnPrimary, Overlay, ConfirmCard, SendRow, ProjTable,
  DetHead, ProjBar, Empty, Spinner,
} from './styles';

// =========================================================
// Endpoints / constantes
// =========================================================
const LIST_ENDPOINT = '/sales-invoice';
const AGING_ENDPOINT = '/invoice-aging-by-project';
const EMIT_ENDPOINT = '/emit-invoice-payment';
const EMIT_BOLETOS_ALL = '/emit-invoice-payments-all';
const MAIL_ENDPOINT = '/invoice-mail';
const CARD_METHODS = ['credit_card_recurring', 'credit_card'];
const BOLETO_METHODS = ['boleto_recurring', 'boleto'];
const isCard = (m: string) => CARD_METHODS.includes(m);
const isBoleto = (m: string) => BOLETO_METHODS.includes(m);
const methodLabel = (m: string) => (isCard(m) ? 'Cartão' : isBoleto(m) ? 'Boleto' : m);

const RATE_MULTA = 0.02;
const RATE_JUROS_MES = 0.01;

const TEMPLATES = [
  { key: 'reminder_before_due', endpoint: 'reminder-before-due', label: 'Lembrete pré-vencimento', aud: 'Aluno', seq: false },
  { key: 'reminder_after_due', endpoint: 'reminder-after-due', label: 'Lembrete pós-vencimento', aud: 'Aluno', seq: true },
  { key: 'collection_letter', endpoint: 'collection-letter', label: 'Carta de cobrança (60D)', aud: 'Aluno', seq: false },
  { key: 'ninety_day_notice', endpoint: 'ninety-day-notice', label: 'Notificação de débito (90D)', aud: 'Aluno', seq: false },
  { key: 'credit_card_reminder', endpoint: 'card-reminder', label: 'Lembrete cartão recorrente', aud: 'Aluno', seq: false },
  { key: 'team_default_notice', endpoint: 'team-default-notice', label: 'Equipe — inadimplência', aud: 'Equipe', seq: false },
  { key: 'team_regularization', endpoint: 'team-regularization', label: 'Equipe — regularização', aud: 'Equipe', seq: false },
];

// =========================================================
// Tipos
// =========================================================
interface CollectionLetter {
  sent_at?: string; ninety_day_sent_at?: string; team_notified_at?: string;
  serasa_status?: 'incluido' | 'baixado'; serasa_at?: string; status?: string;
}
interface Invoice {
  _id: string; invoice_hash: string; installment_number: number; payment_date: string;
  installment_value: number; price?: number; juros?: number; multa?: number; createdAt?: string; approved_at?: string; payment_status_id: string; payment_method_id: string;
  project_id?: string; event_id?: string; sale_hash?: string; payer_id?: string;
  client_id?: string; client_id_response?: { name?: string; document_number?: string };
  boleto_url?: string; collection_letter?: CollectionLetter;
}
interface Charges { base: number; daysOverdue: number; juros: number; multa: number; final: number; }
type RowStatus = 'idle' | 'sending' | 'done' | 'error';
interface AgingBucket { count: number; sum: number; }
interface AgingRow { project_id: string; pagos: AgingBucket; vencidos: AgingBucket; avencer: AgingBucket; }
interface AgingData { projects: AgingRow[]; totals: { pagos: AgingBucket; vencidos: AgingBucket; avencer: AgingBucket }; }
type ProjStatus = 'todas' | 'avencer' | 'vencidas' | 'pagas';
type OverlayState =
  | { kind: 'emitOne'; invoice: Invoice; withInterest: boolean; days: number }
  | { kind: 'emitAll' }
  | { kind: 'boletos' }
  | { kind: 'mail'; invoice: Invoice }
  | { kind: 'mailSend'; invoice: Invoice; tplIx: number; sequence: number }
  | { kind: 'newCharge'; ref: Invoice; name: string; products: { label: string; value: string }[]; product_id: string; value: string; date: string; emit: boolean }
  | null;

// =========================================================
// Helpers
// =========================================================
const formatBRL = (cents: number) =>
  (Number(cents || 0) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatDateBR = (d: string | Date | null | undefined) => {
  if (!d) return '—';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '—';
  const [y, m, day] = dt.toISOString().slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};
// "1.500,00" | "150,00" | "150" | "R$ 150,00" -> centavos (descarta qualquer caractere que não seja dígito ou separador)
const parseCents = (v: string) => {
  const n = Number(String(v || '').replace(/[^0-9,.]/g, '').replace(/\./g, '').replace(',', '.'));
  return isNaN(n) ? 0 : Math.round(n * 100);
};
const invoiceMonth = (d: string) => { const dt = new Date(d); return isNaN(dt.getTime()) ? '' : dt.toISOString().slice(0, 7); };
const currentMonth = () => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`; };
const daysOverdueOf = (due: string) => {
  const d = new Date(due);
  return isNaN(d.getTime()) ? 0 : Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};
const monthsLate = (due: Date): number => {
  if (Date.now() <= due.getTime()) return 0;
  let months = 1; let anchor = addMonths(due, 1);
  while (anchor.getTime() < Date.now()) { anchor = addMonths(anchor, 1); months += 1; }
  return months;
};
const computeCharges = (inv: Invoice): Charges => {
  const base = Number(inv.price ?? inv.installment_value ?? 0);
  const due = inv.payment_date ? new Date(inv.payment_date) : null;
  const d = due ? daysOverdueOf(inv.payment_date) : 0;
  if (!due || d <= 0) return { base, daysOverdue: 0, juros: 0, multa: 0, final: base };
  const months = monthsLate(due);
  const multa = Math.round(base * RATE_MULTA);
  const juros = Math.round(base * RATE_JUROS_MES * months);
  return { base, daysOverdue: d, juros, multa, final: base + juros + multa };
};

// =========================================================
// Componente
// =========================================================
const PainelInadimplencia: React.FC = () => {
  const { addToast } = useToast();
  const { handleApiErrors, user } = useAuth();

  const [tab, setTab] = useState<'faturas' | 'projetos'>('faturas');

  // dados globais
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);           // waiting (todos os projetos) + pagas negativadas
  const [aging, setAging] = useState<AgingData | null>(null);
  const [projectMeta, setProjectMeta] = useState<Record<string, { name: string; ref?: string }>>({});

  // filtros da aba Faturas (global)
  const [mode, setMode] = useState<string>('mes');
  const [monthValue, setMonthValue] = useState<string>(currentMonth());
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [search, setSearch] = useState<string>('');

  // ações
  const [rowState, setRowState] = useState<Record<string, RowStatus>>({});
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [overlay, setOverlay] = useState<OverlayState>(null);

  // aba Projetos
  const [projSearch, setProjSearch] = useState('');
  const [projScreen, setProjScreen] = useState<string>('');          // '' = lista; project_id = tela do projeto
  const [projInvoices, setProjInvoices] = useState<Invoice[]>([]);   // faturas do projeto (todos os status)
  const [projLoading, setProjLoading] = useState(false);
  const [projSubTab, setProjSubTab] = useState<'faturas' | 'inscritos'>('faturas');
  const [projStatus, setProjStatus] = useState<ProjStatus>('todas');
  const [projStudent, setProjStudent] = useState<string>('');
  const [projFatSearch, setProjFatSearch] = useState('');
  const [inscSearch, setInscSearch] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const chargeValueRef = useRef<HTMLInputElement>(null);   // lê o valor direto do campo (autopreenchimento não dispara onChange)

  const pname = useCallback((slug?: string) => (slug && projectMeta[slug]?.name) || slug || '—', [projectMeta]);
  const pref = useCallback((slug?: string) => (slug && projectMeta[slug]?.ref) || '—', [projectMeta]);

  // ---------- cargas ----------
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const waitingCall = await api.get(LIST_ENDPOINT, { params: { limitItems: 1000000, where: { payment_status_id: 'waiting' } } });
      const paidSerasaCall = await api.get(LIST_ENDPOINT, { params: { limitItems: 100000, where: { payment_status_id: 'paid', 'collection_letter.serasa_status': 'incluido' } } });
      setInvoices([...(waitingCall?.data?.rows || []), ...(paidSerasaCall?.data?.rows || [])]);
    } catch (err) {
      addToast({ title: 'Erro ao carregar faturas', type: 'error' });
      handleApiErrors(err);
      setInvoices([]);
    } finally { setLoading(false); }
  }, [addToast, handleApiErrors]);

  const loadAging = useCallback(async () => {
    try { const { data } = await api.get(AGING_ENDPOINT); setAging(data || null); }
    catch { /* silencioso — a aba Projetos degrada para zeros */ }
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const { data } = await api.get('/projects', { params: { limitItems: 100000 } });
      const list: any[] = data?.rows || data || [];
      const meta: Record<string, { name: string; ref?: string }> = {};
      list.forEach((p: any) => {
        const key = p?.url || p?.project_id || p?._id;
        if (key) meta[key] = { name: p?.title || p?.name || key, ref: p?.reference_number };
      });
      setProjectMeta(meta);
    } catch { /* fallback: mostra o slug */ }
  }, []);

  useEffect(() => { load(); loadAging(); loadProjects(); }, [load, loadAging, loadProjects]);

  // faturas de um projeto (todos os status) — carregado ao abrir a tela do projeto
  const openProject = useCallback(async (projectId: string) => {
    setProjScreen(projectId); setProjSubTab('faturas'); setProjStatus('todas'); setProjStudent(''); setProjFatSearch(''); setInscSearch('');
    setProjLoading(true);
    try {
      const { data } = await api.get(LIST_ENDPOINT, { params: { limitItems: 100000, where: { project_id: projectId } } });
      setProjInvoices(data?.rows || []);
    } catch (err) {
      addToast({ title: 'Erro ao carregar o projeto', type: 'error' });
      handleApiErrors(err);
      setProjInvoices([]);
    } finally { setProjLoading(false); }
  }, [addToast, handleApiErrors]);

  // atualiza uma fatura nos dois datasets (global + projeto) após ação
  const patchInvoice = (id: string, cl: Partial<CollectionLetter>) => {
    const upd = (arr: Invoice[]) => arr.map(i => i._id === id ? { ...i, collection_letter: { ...(i.collection_letter || {}), ...cl } } : i);
    setInvoices(upd); setProjInvoices(upd);
  };

  // ---------- filtros globais (aba Faturas) ----------
  const projMatch = useCallback((i: Invoice) => projectFilter === 'all' || i.project_id === projectFilter, [projectFilter]);
  const bucket = {
    avencer: (i: Invoice) => i.payment_status_id === 'waiting' && daysOverdueOf(i.payment_date) <= 0,
    b1_30: (i: Invoice) => i.payment_status_id === 'waiting' && daysOverdueOf(i.payment_date) >= 1 && daysOverdueOf(i.payment_date) <= 30,
    b31_60: (i: Invoice) => i.payment_status_id === 'waiting' && daysOverdueOf(i.payment_date) >= 31 && daysOverdueOf(i.payment_date) <= 60,
    b61_90: (i: Invoice) => i.payment_status_id === 'waiting' && daysOverdueOf(i.payment_date) >= 61 && daysOverdueOf(i.payment_date) <= 90,
    b90: (i: Invoice) => i.payment_status_id === 'waiting' && daysOverdueOf(i.payment_date) > 90,
    negativadas: (i: Invoice) => i.collection_letter?.serasa_status === 'incluido',
    pagasNeg: (i: Invoice) => i.payment_status_id === 'paid' && i.collection_letter?.serasa_status === 'incluido',
  };
  const inModeFn = useCallback((i: Invoice) => {
    if (!projMatch(i)) return false;
    const d = daysOverdueOf(i.payment_date);
    switch (mode) {
      case 'mes': return i.payment_status_id === 'waiting' && invoiceMonth(i.payment_date) === monthValue;
      case 'vencidas': return i.payment_status_id === 'waiting' && d > 0;
      case '60': return i.payment_status_id === 'waiting' && d > 60;
      case '90': return i.payment_status_id === 'waiting' && d > 90;
      case 'historico': return i.payment_status_id === 'waiting';
      case 'serasa_pago': return bucket.pagasNeg(i);
      case 'negativadas': return bucket.negativadas(i);
      case 'avencer': return bucket.avencer(i);
      case 'b1_30': return bucket.b1_30(i);
      case 'b31_60': return bucket.b31_60(i);
      case 'b61_90': return bucket.b61_90(i);
      case 'b90': return bucket.b90(i);
      default: return false;
    }
  }, [mode, monthValue, projMatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices
      .filter(inModeFn)
      .filter(i => !q
        || (i.client_id_response?.name || '').toLowerCase().includes(q)
        || pname(i.project_id).toLowerCase().includes(q)
        || String(i.client_id_response?.document_number || '').toLowerCase().includes(q))
      .sort((a, b) => new Date(a.payment_date).getTime() - new Date(b.payment_date).getTime());
  }, [invoices, inModeFn, search, pname]);
  const cardPending = useMemo(() => rows.filter(i => isCard(i.payment_method_id) && i.payment_status_id === 'waiting' && rowState[i._id] !== 'done' && rowState[i._id] !== 'sending'), [rows, rowState]);
  const rowsTotal = useMemo(() => rows.reduce((a, i) => a + computeCharges(i).final, 0), [rows]);
  const pagasNegCount = invoices.filter(i => projMatch(i) && bucket.pagasNeg(i)).length;

  const changeMode = (k: string) => { setMode(k); setSelected(new Set()); };
  const changeProject = (v: string) => { setProjectFilter(v); setSelected(new Set()); };

  // ---------- emissão / serasa / e-mail ----------
  const emitOne = async (inv: Invoice, withInterest: boolean, days?: number): Promise<boolean> => {
    if (rowState[inv._id] === 'done' || rowState[inv._id] === 'sending') return false;
    setRowState(s => ({ ...s, [inv._id]: 'sending' }));
    try {
      const body: { with_interest: string; additional_days?: number } = { with_interest: withInterest ? 'yes' : 'no' };
      if (isBoleto(inv.payment_method_id) && days) body.additional_days = days;
      const resp = await api.post(`${EMIT_ENDPOINT}/${inv.invoice_hash}`, body);
      const newBoletoUrl = resp?.data?.boleto_url;
      if (isBoleto(inv.payment_method_id) && newBoletoUrl) {
        const upd = (arr: Invoice[]) => arr.map(i => i._id === inv._id ? { ...i, boleto_url: newBoletoUrl } : i);
        setInvoices(upd); setProjInvoices(upd);
      }
      setRowState(s => ({ ...s, [inv._id]: 'done' }));
      return true;
    } catch (err: any) {
      setRowState(s => ({ ...s, [inv._id]: 'error' }));
      addToast({ title: err?.response?.data?.message || 'Erro ao emitir cobrança', type: 'error' });
      return false;
    }
  };
  const confirmEmitOne = async () => {
    if (overlay?.kind !== 'emitOne') return;
    setBusy(true); const ok = await emitOne(overlay.invoice, overlay.withInterest, overlay.days); setBusy(false); setOverlay(null);
    if (ok) addToast({ title: 'Cobrança emitida!', type: 'success' });
  };
  const confirmEmitAll = async () => {
    const list = [...cardPending]; if (!list.length) { setOverlay(null); return; }
    setBusy(true); let ok = 0, fail = 0;
    for (const inv of list) { (await emitOne(inv, true)) ? ok++ : fail++; }
    setBusy(false); setOverlay(null);
    addToast({ title: `${ok} emitida(s)` + (fail ? ` · ${fail} com erro` : ''), type: fail ? 'error' : 'success' });
  };
  const confirmBoletos = async () => {
    setBusy(true);
    try { await api.post(EMIT_BOLETOS_ALL); addToast({ title: 'Geração de boletos disparada', type: 'success' }); await load(); }
    catch (err) { addToast({ title: 'Erro ao gerar boletos', type: 'error' }); handleApiErrors(err); }
    finally { setBusy(false); setOverlay(null); }
  };
  const serasaAction = async (inv: Invoice, action: 'incluir' | 'baixar') => {
    if (action === 'incluir' && !inv.collection_letter?.ninety_day_sent_at) {
      addToast({ title: 'Envie a notificação de 90 dias antes de negativar', type: 'error' }); return;
    }
    try {
      await api.post(`${MAIL_ENDPOINT}/serasa/${inv._id}`, { action });
      if (action === 'incluir') patchInvoice(inv._id, { serasa_status: 'incluido', serasa_at: new Date().toISOString(), status: 'serasa' });
      else patchInvoice(inv._id, { serasa_status: 'baixado' });
      addToast({ title: action === 'incluir' ? 'Marcada no Serasa' : 'Baixa do Serasa registrada', type: 'success' });
    } catch (err: any) { addToast({ title: err?.response?.data?.message || 'Erro ao atualizar Serasa', type: 'error' }); }
  };
  const sendMail = async (inv: Invoice, tplIx: number, sequence: number, test: boolean) => {
    const t = TEMPLATES[tplIx];
    if (test) {
      const to = (testEmail || '').trim();
      if (!to) { addToast({ title: 'Informe o e-mail para o teste', type: 'error' }); return; }
      setBusy(true);
      try {
        await api.post(`${MAIL_ENDPOINT}/test/${inv._id}`, { mail_type: t.key, to, ...(t.seq ? { sequence } : {}) });
        addToast({ title: `Teste enviado para ${to}`, type: 'success' });
      } catch (err: any) { addToast({ title: err?.response?.data?.message || 'Erro ao enviar e-mail', type: 'error' }); }
      finally { setBusy(false); setOverlay(null); }
      return;
    }
    setBusy(true);
    try {
      await api.post(`${MAIL_ENDPOINT}/${t.endpoint}/${inv._id}`, t.seq ? { sequence } : {});
      if (t.key === 'collection_letter') patchInvoice(inv._id, { sent_at: new Date().toISOString() });
      if (t.key === 'ninety_day_notice') patchInvoice(inv._id, { ninety_day_sent_at: new Date().toISOString() });
      addToast({ title: `"${t.label}" enviado`, type: 'success' });
    } catch (err: any) { addToast({ title: err?.response?.data?.message || 'Erro ao enviar e-mail', type: 'error' }); }
    finally { setBusy(false); setOverlay(null); }
  };

  // ---------- nova cobrança avulsa (boleto) para inscrito existente ----------
  // Herda project_id, event_id, sale_hash, cliente e pagador de uma fatura do inscrito no projeto
  // (mesmo desenho do DuplicatePayment da tela de Faturas).
  const openNewCharge = async (name: string, ref: Invoice) => {
    setBusy(true);
    try {
      const { data } = await api.get('/products', { params: { limitItems: 1000, where: { project_id: ref.project_id } } });
      const products = (data?.rows || []).map((p: any) => ({ label: p?.title, value: p?._id?.toString() }));
      const d = new Date(Date.now() + 5 * 86400000);
      const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      setOverlay({ kind: 'newCharge', ref, name, products, product_id: '', value: '', date, emit: true });
    } catch (err) {
      addToast({ title: 'Erro ao carregar produtos do projeto', type: 'error' });
      handleApiErrors(err);
    } finally { setBusy(false); }
  };
  const confirmNewCharge = async () => {
    if (overlay?.kind !== 'newCharge') return;
    const o = overlay;
    const value = parseCents(chargeValueRef.current?.value || o.value);
    // meio-dia de Brasília: evita a data "voltar um dia" ao converter para UTC
    const due = new Date(`${o.date}T12:00:00-03:00`);
    if (!o.product_id) { addToast({ title: 'Selecione o produto', type: 'error' }); return; }
    if (value <= 0) { addToast({ title: 'Informe o valor da cobrança', type: 'error' }); return; }
    if (isNaN(due.getTime()) || due.getTime() <= Date.now()) { addToast({ title: 'O vencimento precisa ser uma data futura', type: 'error' }); return; }

    setBusy(true);
    const invoice_hash = uuid();
    try {
      await api.post(LIST_ENDPOINT, {
        project_id: o.ref.project_id,
        event_id: o.ref.event_id,
        sale_hash: o.ref.sale_hash,
        invoice_hash,
        client_id: o.ref.client_id,
        payer_id: o.ref.payer_id,
        product_id: o.product_id,
        payment_method_id: 'boleto_recurring',
        payment_status_id: 'waiting',
        payment_date: due,
        total: value,
        price: value,
        installment_value: value,
        juros: 0,
        multa: 0,
      });
    } catch (err: any) {
      addToast({ title: err?.response?.data?.message || 'Erro ao criar a fatura', type: 'error' });
      handleApiErrors(err);
      setBusy(false);
      return;
    }

    if (o.emit) {
      try {
        const resp = await api.post(`${EMIT_ENDPOINT}/${invoice_hash}`, { with_interest: 'no' });
        if (resp?.data?.boleto_url) addToast({ title: 'Cobrança criada e boleto emitido!', type: 'success' });
        else addToast({ title: 'Fatura criada, mas o boleto não foi emitido. Emita pela tela de Faturas.', type: 'error' });
      } catch (err: any) {
        addToast({ title: err?.response?.data?.message || 'Fatura criada, mas houve erro ao emitir o boleto', type: 'error' });
      }
    } else {
      addToast({ title: 'Fatura criada (boleto não emitido)', type: 'success' });
    }

    setBusy(false); setOverlay(null);
    if (projScreen) { await openProject(projScreen); setProjStudent(o.name); }
  };

  // ---------- seleção / lote (aba Faturas global) ----------
  const toggleSel = (id: string) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (checked: boolean) => setSelected(checked ? new Set(rows.map(i => i._id)) : new Set());
  const runBulk = async (action: 'emitir' | '60' | '90' | 'serasa') => {
    const items = invoices.filter(i => selected.has(i._id)); setBusy(true);
    try {
      if (action === 'emitir') {
        for (const i of items) if (isCard(i.payment_method_id) && i.payment_status_id === 'waiting') await emitOne(i, true);
        addToast({ title: `${items.length} emissão(ões) disparada(s)`, type: 'success' });
      } else if (action === '60') {
        for (const i of items) { await api.post(`${MAIL_ENDPOINT}/collection-letter/${i._id}`, {}); patchInvoice(i._id, { sent_at: new Date().toISOString() }); }
        addToast({ title: `60D enviado p/ ${items.length}`, type: 'success' });
      } else if (action === '90') {
        const byClient = new Map<string, Invoice>();
        items.forEach(i => { if (i.client_id && !byClient.has(i.client_id)) byClient.set(i.client_id, i); });
        for (const i of Array.from(byClient.values())) { await api.post(`${MAIL_ENDPOINT}/ninety-day-notice/${i._id}`, {}); }
        await load();
        addToast({ title: `90D agrupado por aluno (${byClient.size})`, type: 'success' });
      } else if (action === 'serasa') {
        let bl = 0;
        for (const i of items) {
          if (i.payment_status_id !== 'waiting') continue;
          if (!i.collection_letter?.ninety_day_sent_at) { bl++; continue; }
          await api.post(`${MAIL_ENDPOINT}/serasa/${i._id}`, { action: 'incluir' });
          patchInvoice(i._id, { serasa_status: 'incluido', serasa_at: new Date().toISOString(), status: 'serasa' });
        }
        addToast({ title: bl ? `Marcadas — ${bl} bloqueada(s) sem 90D` : `${items.length} marcada(s) no Serasa`, type: bl ? 'error' : 'success' });
      }
    } catch (err: any) { addToast({ title: err?.response?.data?.message || 'Erro na ação em lote', type: 'error' }); }
    finally { setBusy(false); setSelected(new Set()); }
  };

  // ---------- linha de fatura (reusada) ----------
  const regua = (inv: Invoice) => {
    const cl = inv.collection_letter || {}; const items: React.ReactNode[] = [];
    if (cl.sent_at) items.push(<Selo key="60" className="s60" title={`60D em ${formatDateBR(cl.sent_at)}`}>60D ✓</Selo>);
    if (cl.ninety_day_sent_at) items.push(<Selo key="90" className="s90" title={`90D em ${formatDateBR(cl.ninety_day_sent_at)}`}>90D ✓</Selo>);
    if (cl.serasa_status === 'incluido') items.push(<Selo key="ser" className="sser" title={`Serasa em ${formatDateBR(cl.serasa_at)}`}>⚑ Serasa</Selo>);
    if (cl.serasa_status === 'baixado') items.push(<Selo key="bx" className="sbx">Serasa baixado</Selo>);
    if (inv.payment_status_id === 'paid' && cl.serasa_status === 'incluido') items.push(<Selo key="al" className="alert">pago+neg.</Selo>);
    return items.length ? <>{items}</> : <span className="muted">—</span>;
  };
  const statusCell = (inv: Invoice) => {
    const s = inv.payment_status_id;
    if (s === 'cancelled') return <span className="st cancel" title="Cancelada"><FiX size={15} /></span>;
    if (s === 'paid') return <span className="st paid" title="Paga"><FiCheck size={15} /></span>;
    if (s === 'waiting' && daysOverdueOf(inv.payment_date) > 0) return <span className="st late" title="Em atraso"><FiAlertTriangle size={15} /></span>;
    return <span className="st wait" title="Aguardando pagamento"><FiDollarSign size={15} /></span>;
  };
  const invoiceRow = (inv: Invoice, opts?: { check?: boolean; name?: boolean; projeto?: boolean; num?: boolean; created?: boolean }) => {
    const check = opts?.check === true, name = opts?.name !== false, projeto = opts?.projeto !== false, num = opts?.num === true, created = opts?.created === true;
    const c = computeCharges(inv); const card = isCard(inv.payment_method_id);
    const paid = inv.payment_status_id === 'paid'; const cancelled = inv.payment_status_id === 'cancelled'; const st = rowState[inv._id]; const cl = inv.collection_letter || {};
    const inc = cl.serasa_status === 'incluido'; const can90 = !!cl.ninety_day_sent_at;
    // pagas mostram os encargos GRAVADOS; em aberto, os calculados
    const juros = paid ? Number(inv.juros || 0) : c.juros;
    const multa = paid ? Number(inv.multa || 0) : c.multa;
    const finalValue = paid ? (c.base + juros + multa) : c.final;
    let action: React.ReactNode;
    const emitBtns = (
      <>
        <BtnLink className="plain" disabled={busy} onClick={() => setOverlay({ kind: 'emitOne', invoice: inv, withInterest: false, days: 3 })}>S/ juros</BtnLink>
        <BtnLink className="interest" disabled={busy} onClick={() => setOverlay({ kind: 'emitOne', invoice: inv, withInterest: true, days: 3 })}>{st === 'error' ? 'Repetir c/ juros' : 'C/ juros'}</BtnLink>
      </>
    );
    const verBoleto = inv.boleto_url
      ? <a href={inv.boleto_url} target="_blank" rel="noreferrer" style={{ textDecoration: 'none' }}><BtnLink as="span" className="boleto"><FiExternalLink size={12} /> Ver boleto</BtnLink></a>
      : null;
    if (cancelled) {
      action = <Tag className="cancel"><FiX size={12} /> Cancelada</Tag>;
    } else if (paid) {
      action = <><Tag className="paid">Paga</Tag>{verBoleto}{inc && <BtnLink className="interest" onClick={() => serasaAction(inv, 'baixar')}>Dar baixa</BtnLink>}</>;
    } else if (st === 'sending') { action = <Tag className="muted"><Spinner /> Emitindo…</Tag>; }
    else if (st === 'done') { action = <><Tag className="done"><FiCheck size={12} /> Emitida</Tag>{verBoleto}</>; }
    else if (card) { action = emitBtns; }
    else {
      // boleto (não pago): vencido ganha S/ juros | C/ juros; a vencer mantém "ver boleto" / "não lançado"
      const overdue = c.daysOverdue > 0;
      action = <>{overdue && emitBtns}{verBoleto || (!overdue ? <Tag className="warn">Não lançado</Tag> : null)}</>;
    }
    return (
      <tr key={inv._id} className={`${st === 'done' ? 'done' : ''} ${paid ? 'paidrow' : ''} ${cancelled ? 'cancelled' : ''}`}>
        <td className="stcol">{statusCell(inv)}</td>
        {check && <td><input type="checkbox" checked={selected.has(inv._id)} onChange={() => toggleSel(inv._id)} /></td>}
        {name && <td>{inv.client_id_response?.name || '—'}</td>}
        {num && <td>#{inv.installment_number}</td>}
        {projeto && <td className="muted" title={pname(inv.project_id)}>{pname(inv.project_id)}</td>}
        <td>{methodLabel(inv.payment_method_id)}</td>
        {created && <td>{formatDateBR(inv.createdAt)}</td>}
        <td>{formatDateBR(inv.payment_date)}</td>
        <td>{paid ? formatDateBR(inv.approved_at) : '—'}</td>
        <td className="num">R$ {formatBRL(c.base)}</td>
        <td className="num">R$ {formatBRL(juros)}</td>
        <td className="num">R$ {formatBRL(multa)}</td>
        <td className="num final">R$ {formatBRL(finalValue)}</td>
        <td className={`num days ${!paid && !cancelled && c.daysOverdue > 0 ? 'overdue' : ''}`}>{paid || cancelled ? '—' : (c.daysOverdue > 0 ? `${c.daysOverdue}d` : 'em dia')}</td>
        <td>{regua(inv)}</td>
        <td className="actions">
          {action}
          {!cancelled && <IconBtn title="Enviar e-mail" onClick={() => setOverlay({ kind: 'mail', invoice: inv })}><FiMail size={13} /></IconBtn>}
          {inv.payment_status_id === 'waiting' && (
            <IconBtn className={inc ? 'seron' : ''} disabled={!inc && !can90}
              title={inc ? 'Baixar Serasa' : (can90 ? 'Marcar Serasa' : 'Envie a notificação de 90 dias antes de negativar')}
              onClick={() => serasaAction(inv, inc ? 'baixar' : 'incluir')}><FiFlag size={12} /></IconBtn>
          )}
        </td>
      </tr>
    );
  };
  const gridHead = (opts?: { check?: boolean; name?: boolean; projeto?: boolean; num?: boolean; created?: boolean }) => {
    const check = opts?.check === true, name = opts?.name !== false, projeto = opts?.projeto !== false, num = opts?.num === true, created = opts?.created === true;
    return (
      <thead><tr>
        <th style={{ width: 40 }}>Status</th>
        {check && <th style={{ width: 26 }}><input type="checkbox" checked={rows.length > 0 && rows.every(i => selected.has(i._id))} onChange={e => toggleAll(e.target.checked)} /></th>}
        {name && <th>Cliente</th>}
        {num && <th>Nº</th>}
        {projeto && <th>Projeto</th>}
        <th>Método</th>
        {created && <th>Criação</th>}
        <th>Vencimento</th>
        <th>Pagamento</th>
        <th className="num">Original</th><th className="num">Juros</th><th className="num">Multa</th><th className="num">Final</th><th className="num">Atraso</th>
        <th>Régua</th><th></th>
      </tr></thead>
    );
  };

  // ---------- aba Faturas (global) ----------
  const tileDefs: any[] = [
    ['avencer', 'A vencer', bucket.avencer, ''], ['b1_30', '1–30 dias', bucket.b1_30, ''], ['b31_60', '31–60', bucket.b31_60, ''],
    ['b61_90', '61–90', bucket.b61_90, 'warn'], ['b90', '90+ dias', bucket.b90, 'warn'], ['negativadas', 'Negativadas', bucket.negativadas, 'danger'],
  ];
  const projSelect = (value: string, onChange: (v: string) => void) => (
    <select value={value} onChange={e => onChange(e.target.value)}>
      <option value="all">Todos os projetos</option>
      {Object.keys(projectMeta).sort((a, b) => pname(a).localeCompare(pname(b))).map(s => <option key={s} value={s}>{pname(s)}</option>)}
    </select>
  );

  const renderFaturas = () => (
    <>
      {pagasNegCount > 0 && <Alert onClick={() => setMode('serasa_pago')}>⚠ {pagasNegCount} fatura(s) paga(s) ainda negativada(s) no Serasa</Alert>}
      <Tiles>
        {tileDefs.map(([k, l, f, cls]) => {
          const list = invoices.filter(i => projMatch(i) && f(i));
          return (
            <Tile key={k} className={`${cls} ${mode === k ? 'active' : ''}`} onClick={() => changeMode(k)}>
              <div className="lb">{l}</div><div className="n">{list.length}</div>
              <div className="v">R$ {formatBRL(list.reduce((a, i) => a + computeCharges(i).final, 0))}</div>
            </Tile>
          );
        })}
        {aging && (
          <Tile as="div" className="pago" style={{ cursor: 'default' }}>
            <div className="lb">Pagos (todos)</div>
            <div className="n" style={{ color: '#16a34a' }}>{aging.totals.pagos.count}</div>
            <div className="v">R$ {formatBRL(aging.totals.pagos.sum)}</div>
          </Tile>
        )}
      </Tiles>
      <Filters>
        <Seg>
          {[['mes', 'Mês'], ['vencidas', 'Vencidas'], ['60', '60+ dias'], ['90', '90+ dias'], ['historico', 'Histórico'], ['serasa_pago', 'Pago + Serasa']].map(([k, l]) => (
            <button key={k} className={mode === k ? 'active' : ''} onClick={() => changeMode(k)}>{l}</button>
          ))}
        </Seg>
        {mode === 'mes' && <input type="month" value={monthValue} onChange={e => setMonthValue(e.target.value)} />}
        {projSelect(projectFilter, changeProject)}
        <input type="search" placeholder="Buscar cliente, projeto ou documento…" value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 220 }} />
        <SummaryRight>{rows.length} fatura(s) · total R$ {formatBRL(rowsTotal)}</SummaryRight>
      </Filters>

      {selected.size > 0 && (
        <BulkBar>
          <b>{selected.size}</b> selecionada(s)<div className="spacer" />
          <BulkBtn className="primary" disabled={busy} onClick={() => runBulk('emitir')}>Emitir</BulkBtn>
          <BulkBtn disabled={busy} onClick={() => runBulk('60')}>Enviar 60D</BulkBtn>
          <BulkBtn disabled={busy} onClick={() => runBulk('90')}>Enviar 90D</BulkBtn>
          <BulkBtn className="danger" disabled={busy} onClick={() => runBulk('serasa')}>Marcar Serasa</BulkBtn>
          <BulkBtn disabled={busy} onClick={() => setSelected(new Set())}>Limpar</BulkBtn>
        </BulkBar>
      )}

      <TableWrap>
        <Grid>{gridHead({ check: true, num: true })}<tbody>{rows.map(i => invoiceRow(i, { check: true, num: true }))}</tbody></Grid>
        {!loading && rows.length === 0 && <Empty>Nenhuma fatura nesta visão.</Empty>}
        {loading && <Empty><Spinner /> Carregando…</Empty>}
      </TableWrap>
    </>
  );

  // ---------- aba Projetos: lista ----------
  const renderProjetosLista = () => {
    const q = projSearch.trim().toLowerCase();
    const slugs = Array.from(new Set([...Object.keys(projectMeta), ...(aging?.projects || []).map(p => p.project_id)]));
    const list = slugs
      .filter(slug => !q || pname(slug).toLowerCase().includes(q) || String(pref(slug)).toLowerCase().includes(q))
      .map(slug => {
        const a = aging?.projects.find(p => p.project_id === slug);
        const pg = a?.pagos || { count: 0, sum: 0 }, vc = a?.vencidos || { count: 0, sum: 0 }, av = a?.avencer || { count: 0, sum: 0 };
        const tot = (pg.count + vc.count + av.count) || 1;
        return { slug, pg, vc, av, w: (n: number) => Math.round((n / tot) * 100) };
      })
      .sort((a, b) => (b.vc.count - a.vc.count) || pname(a.slug).localeCompare(pname(b.slug)));
    return (
      <>
        <Filters><input type="search" placeholder="Buscar projeto ou nº de referência…" value={projSearch} onChange={e => setProjSearch(e.target.value)} style={{ minWidth: 300 }} /></Filters>
        <TableWrap>
          <ProjTable>
            <thead><tr><th>Nº ref.</th><th>Projeto</th><th className="num">Pagos</th><th className="num">Vencidos</th><th className="num">A vencer</th><th style={{ width: 140 }}>Saúde</th></tr></thead>
            <tbody>
              {list.map(p => (
                <tr key={p.slug} onClick={() => openProject(p.slug)}>
                  <td><span className="refnum">{pref(p.slug)}</span></td>
                  <td style={{ fontWeight: 600, color: '#111827' }}>{pname(p.slug)}</td>
                  <td className="num pgc">{p.pg.count}<span className="cv">R$ {formatBRL(p.pg.sum)}</span></td>
                  <td className="num vcc">{p.vc.count}<span className="cv">R$ {formatBRL(p.vc.sum)}</span></td>
                  <td className="num avc">{p.av.count}<span className="cv">R$ {formatBRL(p.av.sum)}</span></td>
                  <td><ProjBar><i style={{ width: `${p.w(p.pg.count)}%`, background: '#16a34a' }} /><i style={{ width: `${p.w(p.vc.count)}%`, background: '#dc2626' }} /><i style={{ width: `${p.w(p.av.count)}%`, background: '#0ea5e9' }} /></ProjBar></td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>Nenhum projeto encontrado.</td></tr>}
            </tbody>
          </ProjTable>
        </TableWrap>
        {!aging && <Empty><Spinner /> Carregando agregações…</Empty>}
      </>
    );
  };

  // ---------- aba Projetos: tela do projeto ----------
  const renderProjeto = () => {
    const byStatus = (i: Invoice) => {
      const d = daysOverdueOf(i.payment_date);
      if (projStatus === 'pagas') return i.payment_status_id === 'paid';
      if (projStatus === 'vencidas') return i.payment_status_id === 'waiting' && d > 0;
      if (projStatus === 'avencer') return i.payment_status_id === 'waiting' && d <= 0;
      return true;
    };
    const fq = projFatSearch.trim().toLowerCase();
    const list = projInvoices
      .filter(byStatus)
      .filter(i => !projStudent || (i.client_id_response?.name || '') === projStudent)
      .filter(i => !fq
        || (i.client_id_response?.name || '').toLowerCase().includes(fq)
        || String(i.client_id_response?.document_number || '').toLowerCase().includes(fq)
        || String(i.installment_number || '').includes(fq))
      .sort((a, b) => new Date(a.payment_date).getTime() - new Date(b.payment_date).getTime());

    const map: Record<string, { name: string; inv: Invoice[] }> = {};
    projInvoices.forEach(i => { const k = i.client_id_response?.name || '(sem nome)'; (map[k] = map[k] || { name: k, inv: [] }).inv.push(i); });
    const iq = inscSearch.trim().toLowerCase();
    let students = Object.values(map).map(s => ({
      ...s,
      atraso: s.inv.some(i => i.payment_status_id === 'waiting' && daysOverdueOf(i.payment_date) > 0),
      aberto: s.inv.filter(i => i.payment_status_id === 'waiting').reduce((a, i) => a + computeCharges(i).final, 0),
      nfat: s.inv.length,
    }));
    if (iq) students = students.filter(s => s.name.toLowerCase().includes(iq));
    students.sort((a, b) => (Number(b.atraso) - Number(a.atraso)) || a.name.localeCompare(b.name));

    return (
      <>
        <DetHead>
          <BtnCancel onClick={() => setProjScreen('')}><FiChevronLeft size={14} /> Projetos</BtnCancel>
          <div className="dettitle"><b>{pname(projScreen)}</b> <span style={{ color: '#9ca3af', fontSize: 12 }}>Nº {pref(projScreen)}</span></div>
          <span className="right">{projInvoices.length} fatura(s)</span>
        </DetHead>

        <Tabs style={{ padding: 0, marginBottom: 12 }}>
          <button className={projSubTab === 'faturas' ? 'active' : ''} onClick={() => setProjSubTab('faturas')}>Faturas</button>
          <button className={projSubTab === 'inscritos' ? 'active' : ''} onClick={() => { setProjSubTab('inscritos'); setProjStudent(''); }}>Inscritos</button>
        </Tabs>

        {projLoading && <Empty><Spinner /> Carregando…</Empty>}

        {!projLoading && projSubTab === 'faturas' && (
          <>
            <Filters>
              <Seg>
                <button className={projStatus === 'todas' ? 'active' : ''} onClick={() => setProjStatus('todas')}>Todas</button>
                <button className={projStatus === 'avencer' ? 'active' : ''} onClick={() => setProjStatus('avencer')}>A vencer</button>
                <button className={projStatus === 'vencidas' ? 'active' : ''} onClick={() => setProjStatus('vencidas')}>Vencidas</button>
                <button className={projStatus === 'pagas' ? 'active' : ''} onClick={() => setProjStatus('pagas')}>Pagas</button>
              </Seg>
              {projStudent && <Chip>Inscrito: {projStudent} <button onClick={() => setProjStudent('')}><FiX size={11} /></button></Chip>}
              <input type="search" placeholder="Buscar inscrito, documento ou nº…" value={projFatSearch} onChange={e => setProjFatSearch(e.target.value)} style={{ minWidth: 220 }} />
              <SummaryRight>{list.length} fatura(s)</SummaryRight>
            </Filters>
            <TableWrap>
              <Grid>{gridHead({ projeto: false, num: true, created: true })}<tbody>{list.map(i => invoiceRow(i, { projeto: false, num: true, created: true }))}</tbody></Grid>
              {list.length === 0 && <Empty>Nenhuma fatura nesta visão.</Empty>}
            </TableWrap>
          </>
        )}

        {!projLoading && projSubTab === 'inscritos' && (
          <>
            <Filters><input type="search" placeholder="Buscar inscrito…" value={inscSearch} onChange={e => setInscSearch(e.target.value)} style={{ minWidth: 260 }} /></Filters>
            <TableWrap>
              <ProjTable>
                <thead><tr><th style={{ width: 130 }}></th><th>Inscrito</th><th className="num">Faturas</th><th className="num">Em aberto</th><th>Status</th></tr></thead>
                <tbody>
                  {students.map(s => (
                    <tr key={s.name} onClick={() => { setProjStudent(s.name); setProjStatus('todas'); setProjSubTab('faturas'); }}>
                      <td>
                        <BtnLink className="plain" disabled={busy}
                          onClick={e => { e.stopPropagation(); openNewCharge(s.name, s.inv.find(i => i.client_id) || s.inv[0]); }}>
                          <FiDollarSign size={12} /> Nova cobrança
                        </BtnLink>
                      </td>
                      <td style={{ fontWeight: 600, color: '#111827' }}>{s.name}</td>
                      <td className="num">{s.nfat}</td>
                      <td className="num">R$ {formatBRL(s.aberto)}</td>
                      <td>{s.atraso ? <Tag className="late">Em atraso</Tag> : <Tag className="done">Em dia</Tag>}</td>
                    </tr>
                  ))}
                  {students.length === 0 && <tr><td colSpan={5}style={{ textAlign: 'center', color: '#9ca3af', padding: 30 }}>Nenhum inscrito encontrado.</td></tr>}
                </tbody>
              </ProjTable>
            </TableWrap>
          </>
        )}
      </>
    );
  };

  // ---------- overlays ----------
  const renderOverlay = () => {
    if (!overlay) return null;
    const close = (e: React.MouseEvent) => { if (e.target === e.currentTarget) setOverlay(null); };
    if (overlay.kind === 'emitOne') {
      const c = computeCharges(overlay.invoice); const wi = overlay.withInterest;
      const juros = wi ? c.juros : 0, multa = wi ? c.multa : 0, total = c.base + juros + multa;
      return (
        <Overlay onClick={close}><ConfirmCard>
          <div className="head"><div className="ht"><h4>{wi ? 'Emitir com juros' : 'Emitir sem juros'}</h4><p>{overlay.invoice.client_id_response?.name} · {methodLabel(overlay.invoice.payment_method_id)}</p></div></div>
          <div className="rows">
            <div className="r"><span style={{color: '#333'}}>Valor original</span><span style={{color: '#333'}}>R$ {formatBRL(c.base)}</span></div>
            <div className="r"><span style={{color: '#333'}}>Juros{c.daysOverdue ? ` (${c.daysOverdue}d)` : ''}</span><span style={{color: '#333'}}>R$ {formatBRL(juros)}</span></div>
            <div className="r"><span style={{color: '#333'}}>Multa</span><span style={{color: '#333'}}>R$ {formatBRL(multa)}</span></div>
            <div className="r total"><span style={{color: '#333'}}>Total a cobrar</span><span style={{color: '#333'}}>R$ {formatBRL(total)}</span></div>
            {isBoleto(overlay.invoice.payment_method_id) && (
              <div className="r"><span style={{color: '#333'}}>Vencer em</span>
                <span>
                  <input type="number" min={1} value={overlay.days}
                    onChange={e => setOverlay({ ...overlay, days: Math.max(1, Number(e.target.value) || 1) })}
                    style={{ width: 60, height: 30, padding: '0 8px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', textAlign: 'right' }} /> dias
                  <small style={{ color: '#9ca3af', marginLeft: 6 }}>({formatDateBR(new Date(Date.now() + overlay.days * 86400000))})</small>
                </span>
              </div>
            )}
          </div>
          <div className="foot"><BtnCancel disabled={busy} onClick={() => setOverlay(null)}>Cancelar</BtnCancel><BtnPrimary disabled={busy} onClick={confirmEmitOne}><FiCheck size={14} /> Confirmar</BtnPrimary></div>
        </ConfirmCard></Overlay>
      );
    }
    if (overlay.kind === 'emitAll') {
      const total = cardPending.reduce((a, i) => a + computeCharges(i).final, 0);
      return (
        <Overlay onClick={close}><ConfirmCard>
          <div className="head"><div className="ht"><h4>Emitir todas · com juros</h4><p>{cardPending.length} de cartão na visão atual</p></div></div>
          <div className="rows"><div className="r total"><span>Total estimado</span><span>R$ {formatBRL(total)}</span></div></div>
          <div className="warnbox">⚠ Ação em massa — sem desfazer.</div>
          <div className="foot"><BtnCancel disabled={busy} onClick={() => setOverlay(null)}>Cancelar</BtnCancel><BtnPrimary disabled={busy} onClick={confirmEmitAll}>Emitir {cardPending.length}</BtnPrimary></div>
        </ConfirmCard></Overlay>
      );
    }
    if (overlay.kind === 'boletos') {
      return (
        <Overlay onClick={close}><ConfirmCard>
          <div className="head"><div className="ht"><h4>Gerar boletos não lançados</h4></div></div>
          <div className="warnbox">⚠ Gera todos os boletos recorrentes ainda não lançados com vencimento até o próximo mês — não se limita ao mês/projeto filtrado. Sem desfazer.</div>
          <div className="foot"><BtnCancel disabled={busy} onClick={() => setOverlay(null)}>Cancelar</BtnCancel><BtnPrimary disabled={busy} onClick={confirmBoletos}>Gerar boletos</BtnPrimary></div>
        </ConfirmCard></Overlay>
      );
    }
    if (overlay.kind === 'mail') {
      const inv = overlay.invoice;
      return (
        <Overlay onClick={close}><ConfirmCard className="wide">
          <div className="head"><div className="ht"><h4>Enviar e-mail da fatura</h4><p>{inv.client_id_response?.name} · {methodLabel(inv.payment_method_id)}</p></div><button className="x" onClick={() => setOverlay(null)}><FiX size={16} /></button></div>
          <div className="mailbody">
            {TEMPLATES.map((t, ix) => (
              <button key={t.key} className="tplcard" onClick={() => { setTestEmail(user?.email || ''); setOverlay({ kind: 'mailSend', invoice: inv, tplIx: ix, sequence: 1 }); }}>
                <div className="tr"><span className="tt">{t.label}</span><span className={`aud ${t.aud === 'Equipe' ? 'equipe' : 'aluno'}`}>{t.aud}</span></div>
              </button>
            ))}
          </div>
          <div className="foot"><BtnCancel onClick={() => setOverlay(null)}>Fechar</BtnCancel></div>
        </ConfirmCard></Overlay>
      );
    }
    if (overlay.kind === 'mailSend') {
      const t = TEMPLATES[overlay.tplIx]; const inv = overlay.invoice;
      return (
        <Overlay onClick={close}><ConfirmCard>
          <div className="head"><div className="ht"><h4>{t.label}</h4><p>{inv.client_id_response?.name}</p></div><button className="x" onClick={() => setOverlay(null)}><FiX size={16} /></button></div>
          <div className="rows">
            <div className="r"><span>E-mail do teste</span>
              <input type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="e-mail@exemplo.com"
                style={{ flex: 1, marginLeft: 12, height: 32, padding: '0 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontFamily: 'inherit' }} />
            </div>
            {t.seq && (
              <div className="r"><span>Envio</span>
                <span className="seq">
                  <button className={overlay.sequence === 1 ? 'active' : ''} onClick={() => setOverlay({ ...overlay, sequence: 1 })}>1º envio</button>
                  <button className={overlay.sequence === 2 ? 'active' : ''} onClick={() => setOverlay({ ...overlay, sequence: 2 })}>2º envio</button>
                </span></div>
            )}
          </div>
          <SendRow>
            <button className="test" disabled={busy} onClick={() => sendMail(inv, overlay.tplIx, overlay.sequence, true)}>✉ Enviar teste</button>
            <button className="real" disabled={busy} onClick={() => sendMail(inv, overlay.tplIx, overlay.sequence, false)}><FiSend size={13} /> Enviar</button>
          </SendRow>
        </ConfirmCard></Overlay>
      );
    }
    if (overlay.kind === 'newCharge') {
      const o = overlay;
      const fieldStyle: React.CSSProperties = { height: 32, padding: '0 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontFamily: 'inherit', minWidth: 200 };
      return (
        <Overlay onClick={close}><ConfirmCard>
          <div className="head"><div className="ht"><h4>Nova cobrança avulsa</h4><p>{o.name} · {pname(o.ref.project_id)} · Boleto</p></div><button className="x" onClick={() => setOverlay(null)}><FiX size={16} /></button></div>
          <div className="rows">
            <div className="r"><span>Produto</span>
              <select value={o.product_id} onChange={e => setOverlay({ ...o, product_id: e.target.value })} style={fieldStyle}>
                <option value="">Selecione…</option>
                {o.products.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div className="r"><span>Valor (R$)</span>
              {/* máscara em real: os dígitos digitados são centavos (1 -> 0,01 · 15000 -> 150,00) */}
              <input ref={chargeValueRef} type="text" inputMode="numeric" autoComplete="off" name="charge_value" placeholder="0,00" value={o.value}
                onChange={e => { const digits = e.target.value.replace(/\D/g, '').replace(/^0+/, ''); setOverlay({ ...o, value: digits ? formatBRL(Number(digits)) : '' }); }}
                style={{ ...fieldStyle, textAlign: 'right' }} />
            </div>
            <div className="r"><span>Vencimento</span>
              <input type="date" value={o.date} onChange={e => setOverlay({ ...o, date: e.target.value })} style={fieldStyle} />
            </div>
            <div className="r"><span>Emitir boleto agora</span>
              <input type="checkbox" checked={o.emit} onChange={e => setOverlay({ ...o, emit: e.target.checked })} />
            </div>
            <div className="r total"><span style={{ color: '#333' }}>Total a cobrar</span><span style={{ color: '#333' }}>R$ {formatBRL(parseCents(o.value))}</span></div>
          </div>
          <div className="warnbox">Sem juros/multa. O boleto é enviado por e-mail ao inscrito ao ser emitido.</div>
          <div className="foot"><BtnCancel disabled={busy} onClick={() => setOverlay(null)}>Cancelar</BtnCancel><BtnPrimary disabled={busy} onClick={confirmNewCharge}><FiCheck size={14} /> Criar cobrança</BtnPrimary></div>
        </ConfirmCard></Overlay>
      );
    }
    return null;
  };

  // =========================================================
  return (
    <Container>
      <Header><div className="t"><h3>Painel de inadimplência</h3><p>Emissão, régua (60D/90D), Serasa e saúde por projeto.</p></div></Header>
      <Tabs>
        <button className={tab === 'faturas' ? 'active' : ''} onClick={() => setTab('faturas')}>Faturas</button>
        <button className={tab === 'projetos' ? 'active' : ''} onClick={() => { setProjScreen(''); setTab('projetos'); }}>Projetos</button>
      </Tabs>
      <Body>
        {tab === 'faturas' && renderFaturas()}
        {tab === 'projetos' && (projScreen ? renderProjeto() : renderProjetosLista())}
      </Body>
      {tab === 'faturas' && (
        <Footer>
          <BtnCancel disabled={loading || busy} onClick={() => { load(); loadAging(); }}><FiRefreshCw size={13} /> Recarregar</BtnCancel>
          <div className="spacer" />
          <BtnCancel disabled={busy} onClick={() => setOverlay({ kind: 'boletos' })}>Gerar boletos não lançados</BtnCancel>
          <BtnPrimary disabled={busy || cardPending.length === 0} onClick={() => setOverlay({ kind: 'emitAll' })}><FiSend size={14} /> Emitir todas com juros {cardPending.length ? `(${cardPending.length})` : ''}</BtnPrimary>
        </Footer>
      )}
      {renderOverlay()}
    </Container>
  );
};

export default PainelInadimplencia;
